import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router";
import { useAuth } from "../context/AuthContext";
import useApi from "../customHooks/useAPI";

const PopperContainer = ({ children }) => <div className="z-50 relative">{children}</div>;

const Consultation = () => {
    const { patientId } = useParams();
    const [consultations, setConsultations] = useState([]);
    const [formData, setFormData] = useState({
        id: null,
        google_event_id: "",
        google_meet_link: "",
        title: "",
        description: "",
        date: null,
        start_time: null,
        end_time: null,
    });
    const [loading, setLoading] = useState(false);
    const { doctorId, logout } = useAuth();
    const navigate = useNavigate();
    const {callApi} = useApi();

    const fetchConsultations = async () => {
        try {
            const data = await callApi({
                url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/doctor/consultationSchedules`,
                headers: { "doctor-id": doctorId } 
            });
            const filteredData = data.data.filter(item=>item.patient_id == patientId)
            setConsultations(filteredData || []);
        } catch (error) {
            console.error("Error fetching consultations:", error);
        }
    };

    useEffect(() => {
        fetchConsultations();
    }, []);

    const createConsultation = async (payload) => {
        try {
            const res = await callApi({
                url:`${process.env.REACT_APP_BACKEND_ENDPOINT}/doctor/createConsultationSchedule`, 
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "doctor-id": doctorId,
                },
                body: JSON.stringify(payload),
            });
            if (res.status === 500){
                logout();
                navigate('/login');
            }
        } catch (error) {
            console.error(error);
        }
    };

    const updateConsultation = async (payload) => {
        try {
            await callApi({
                url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/doctor/updateConsultationSchedule`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "doctor-id": doctorId,
                },
                body: JSON.stringify(payload),
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.date || !formData.start_time || !formData.end_time) return;
        setLoading(true);

        // Combine date + times
        const startDateTime = new Date(formData.date);
        startDateTime.setHours(formData.start_time.getHours());
        startDateTime.setMinutes(formData.start_time.getMinutes());

        const endDateTime = new Date(formData.date);
        endDateTime.setHours(formData.end_time.getHours());
        endDateTime.setMinutes(formData.end_time.getMinutes());

        // Base payload
        const payload = {
            id: formData.id,
            doctor_id: doctorId,
            patient_id: patientId,
            title: formData.title,
            description: formData.description,
            start_time: startDateTime,
            end_time: endDateTime,
            time_zone: "IST",
            google_event_id: formData.google_event_id,
            google_meet_link: formData.google_meet_link,
        };

        try {
            if (formData.id) {
                await updateConsultation(payload);
            } else {
                await createConsultation(payload);
            }
            setFormData({
                id: null,
                google_event_id: "",
                google_meet_link: "",
                title: "",
                description: "",
                date: null,
                start_time: null,
                end_time: null,
            });
            fetchConsultations();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Edit handler
    const handleEdit = (meeting) => {
        const start = new Date(meeting.start_time);
        const end = new Date(meeting.end_time);
        setFormData({
            id: meeting.id,
            google_event_id: meeting.google_event_id || "",
            google_meet_link: meeting.google_meet_link || "",
            title: meeting.title,
            description: meeting.description,
            date: new Date(meeting.start_time),
            start_time: start,
            end_time: end,
        });
    };

    // ✅ Handle Start Time (auto end +30min)
    const handleStartTimeChange = (time) => {
        const newEndTime = new Date(time.getTime() + 30 * 60000);
        setFormData((prev) => ({
            ...prev,
            start_time: time,
            end_time: prev.end_time ? prev.end_time : newEndTime,
        }));
    };

    return (
        <div className="pt-24 p-8 h-full">
            <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                Schedule Consultation
            </h1>

            <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto">
                {/* Left: Consultation List */}
                <div className="lg:w-2/3 bg-white shadow-lg rounded-2xl p-6 overflow-y-auto max-h-[80vh]">
                    <h2 className="text-xl font-semibold text-gray-700 mb-4 border-b pb-2">
                        Scheduled Consultations
                    </h2>

                    {consultations.length === 0 ? (
                        <p className="text-gray-500 text-center">No consultations scheduled.</p>
                    ) : (
                        <div className="divide-y">
                            {consultations.map((meeting) => (
                                <div
                                    key={meeting.id}
                                    className="py-4 flex flex-col sm:flex-row sm:items-center justify-between hover:bg-gray-50 transition rounded-lg px-2"
                                >
                                    <div>
                                        <h3 className="font-semibold text-gray-800">{meeting.title}</h3>
                                        <p className="text-sm text-gray-600">
                                            {new Date(meeting.start_time).toLocaleDateString()} —{" "}
                                            {new Date(meeting.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} to{" "}
                                            {new Date(meeting.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                        </p>
                                        <p className="text-sm text-gray-500 mt-1">{meeting.description}</p>
                                        {meeting.google_meet_link && (
                                            <a
                                                href={meeting.google_meet_link}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 underline text-sm mt-1 inline-block"
                                            >
                                                Join Meet
                                            </a>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleEdit(meeting)}
                                        className="mt-3 sm:mt-0 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm transition-all"
                                    >
                                        Edit
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right: Consultation Form */}
                <div className="lg:w-1/3 bg-white shadow-lg rounded-2xl p-8 flex-shrink-0">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-700 border-b pb-2 mb-4">
                            {formData.id ? "Update Consultation" : "Schedule New Consultation"}
                        </h2>

                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                                type="text"
                                placeholder="Enter consultation title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                required
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                placeholder="Enter consultation description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                rows="3"
                            />
                        </div>

                        {/* Date + Time */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-1">Date</label>
                                <DatePicker
                                    selected={formData.date}
                                    onChange={(date) => setFormData({ ...formData, date })}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="Select Date"
                                    className="border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-blue-400"
                                    popperPlacement="bottom-start"
                                    popperContainer={PopperContainer}
                                    minDate={new Date()}
                                />
                            </div>

                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-1">Start Time</label>
                                <DatePicker
                                    selected={formData.start_time}
                                    onChange={handleStartTimeChange}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    timeCaption="Start"
                                    dateFormat="h:mm aa"
                                    placeholderText="Start Time"
                                    className="border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-blue-400"
                                    popperPlacement="bottom-start"
                                    popperContainer={PopperContainer}
                                />
                            </div>

                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-1">End Time</label>
                                <DatePicker
                                    selected={formData.end_time}
                                    onChange={(time) => setFormData({ ...formData, end_time: time })}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    timeCaption="End"
                                    dateFormat="h:mm aa"
                                    placeholderText="End Time"
                                    className="border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-blue-400"
                                    popperPlacement="bottom-start"
                                    popperContainer={PopperContainer}
                                />
                            </div>
                        </div>

                        {/* Submit */}
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow-md transition-all"
                            >
                                {loading ? "Saving..." : formData.id ? "Update Consultation" : "Create Consultation"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

    );
};

export default Consultation;
